/** @jsxImportSource @emotion/react */
import React, { useState } from 'react';
import { Modal, Descriptions, Tag, Space, Typography, Divider, Empty, Spin, Button, Select, message } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { css } from '@emotion/react';
import { commonStyles } from '../../styles/commonStyles';
import { useGetEventByIdQuery, useUpdateEventMutation } from '../../api/plantingEventsApi';
import { skipToken } from '@reduxjs/toolkit/query';
import { Trans, useTranslation } from 'react-i18next';
import { formatDate, formatDateTime } from '../../i18n';
import { eventStatusLabel } from './utils/eventFormatters';
import { EventKindTag } from '../../utils/eventKindHelpers';
import { useGetSaveUsersQuery } from '../../api/saveUsersApi';
import { useSave } from '../../contexts/WorldContext';

const { Text, Title } = Typography;

interface EventDetailsModalProps {
  eventId: string | null;
  farmId: string;
  open: boolean;
  onClose: () => void;
  canEdit?: boolean; // Право на редактирование события
}

/**
 * Модалка для просмотра деталей события (задачи)
 */
export const EventDetailsModal: React.FC<EventDetailsModalProps> = ({
  eventId,
  farmId,
  open,
  onClose,
  canEdit = false,
}) => {
  const { t } = useTranslation('events');
  const save = useSave();
  const [isEditingAssignment, setIsEditingAssignment] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  const { data: event, isLoading } = useGetEventByIdQuery(
    eventId && open ? { saveId: save.id, eventId } : skipToken
  );

  const { data: farmUsers } = useGetSaveUsersQuery(save.id, { skip: !open || !canEdit });

  const [updateEvent, { isLoading: isUpdating }] = useUpdateEventMutation();

  const getStatusTag = (status?: string) => {
    const statusConfig: Record<string, { color: string }> = {
      planned: { color: 'default' },
      pending: { color: 'orange' },
      in_progress: { color: 'blue' },
      completed: { color: 'green' },
      cancelled: { color: 'red' },
    };
    const key = status && statusConfig[status] ? status : 'planned';
    return <Tag color={statusConfig[key].color}>{eventStatusLabel(key)}</Tag>;
  };

  const getPlantingTypeTag = (type?: string) => {
    if (type === 'batch') {
      return <Tag color="purple">{t('details.batchPlanting')}</Tag>;
    }
    if (type === 'single') {
      return <Tag color="blue">{t('details.singlePlanting')}</Tag>;
    }
    return null;
  };

  const getDecisionTag = (decisionState?: string) => {
    if (decisionState === 'proposed') return <Tag color="gold">{t('decision.proposed')}</Tag>;
    if (decisionState === 'rejected') return <Tag color="red">{t('decision.rejected')}</Tag>;
    return <Tag color="green">{t('decision.confirmed')}</Tag>;
  };

  return (
    <Modal
      title={event ? t('details.title', { id: event.id }) : t('details.titleFallback')}
      open={open}
      onCancel={onClose}
      footer={null}
      width={800}
    >
      {isLoading ? (
        <div css={css([commonStyles.textCenter, { padding: '40px' }])}>
          <Spin size="large" />
        </div>
      ) : !event ? (
        <Empty description={t('details.notFound')} />
      ) : (
        <Space direction="vertical" css={commonStyles.fullWidth} size="large">
          {/* Основная информация */}
          <div>
            <Title level={5}>{t('details.mainInfo')}</Title>
            <Descriptions column={1} bordered size="small">
              <Descriptions.Item label={t('common:labels.status')}>
                {getStatusTag(event.status)}
              </Descriptions.Item>
              <Descriptions.Item label={t('common:labels.type')}>
                <Space>
                  <Tag>{event.type}</Tag>
                  <EventKindTag kind={event.kind} showIcon={true} />
                </Space>
              </Descriptions.Item>
              {event.templateName && (
                <Descriptions.Item label={t('details.template')}>
                  <Text strong>{event.templateName}</Text>
                  {event.templateDescription && (
                    <>
                      <br />
                      <Text type="secondary" css={commonStyles.tinyText}>
                        {event.templateDescription}
                      </Text>
                    </>
                  )}
                </Descriptions.Item>
              )}
              <Descriptions.Item label={t('details.plannedDate')}>
                {event.plannedDate
                  ? formatDate(event.plannedDate, 'short')
                  : '—'}
              </Descriptions.Item>
              {event.actualDate && (
                <Descriptions.Item label={t('batchTable.actualDate')}>
                  {formatDateTime(event.actualDate, 'short')}
                </Descriptions.Item>
              )}
              {event.notes && (
                <Descriptions.Item label={t('form.notes')}>
                  {event.notes}
                </Descriptions.Item>
              )}
              <Descriptions.Item label={t('details.reviewStatus')}>
                <Space>
                  {getDecisionTag(event.decisionState)}
                  {event.sourceType && <Tag>{event.sourceType}</Tag>}
                  {event.sourceConfidence !== null && event.sourceConfidence !== undefined && (
                    <Tag>{Math.round(Number(event.sourceConfidence) * 100)}%</Tag>
                  )}
                </Space>
              </Descriptions.Item>
              {event.decisionNote && (
                <Descriptions.Item label={t('details.decisionComment')}>
                  {event.decisionNote}
                </Descriptions.Item>
              )}
            </Descriptions>
          </div>

          {/* Информация о культуре и посадке */}
          {(event.cropName || event.plantingType) && (
            <div>
              <Title level={5}>{t('details.cropAndPlanting')}</Title>
              <Descriptions column={1} bordered size="small">
                {event.cropName && (
                  <Descriptions.Item label={t('details.crop')}>
                    <Text strong>{event.cropName}</Text>
                    {event.cropLatinName && (
                      <>
                        <br />
                        <Text type="secondary" italic css={commonStyles.tinyText}>
                          {event.cropLatinName}
                        </Text>
                      </>
                    )}
                  </Descriptions.Item>
                )}
                {(event.fieldName || event.plotName || event.bedName) && (
                  <Descriptions.Item label={t('batchTable.location')}>
                    <Space direction="vertical" size={0}>
                      {event.bedName && (
                        <Text strong>{event.bedName}</Text>
                      )}
                      {event.plotName && (
                        <Text type={event.bedName ? 'secondary' : undefined}>
                          {event.plotName}
                        </Text>
                      )}
                      {event.fieldName && (
                        <Text type={event.bedName || event.plotName ? 'secondary' : undefined}>
                          {event.fieldName}
                        </Text>
                      )}
                    </Space>
                  </Descriptions.Item>
                )}
                {event.plantingType && (
                  <Descriptions.Item label={t('details.plantingType')}>
                    {getPlantingTypeTag(event.plantingType)}
                  </Descriptions.Item>
                )}
                {event.plantingType === 'batch' && event.plantingQuantity && (
                  <Descriptions.Item label={t('details.batchQuantity')}>
                    <Trans t={t} i18nKey="details.unitsCount" count={event.plantingQuantity} components={{ b: <Text strong /> }} />
                  </Descriptions.Item>
                )}
                {event.quantityAffected && (
                  <Descriptions.Item label={t('details.unitsAffected')}>
                    <Text strong css={css({ color: '#1890ff' })}>
                      {event.quantityAffected}
                    </Text>{' '}
                    {event.plantingQuantity && t('propertyForm.ofTotal', { total: event.plantingQuantity })}
                  </Descriptions.Item>
                )}
              </Descriptions>
            </div>
          )}

          {/* Назначение и выполнение */}
          <div>
            <Title level={5}>
              {t('details.assignmentAndCompletion')}
              {canEdit && event.status !== 'completed' && event.status !== 'cancelled' && (
                <Button
                  type="link"
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => {
                    setIsEditingAssignment(!isEditingAssignment);
                    if (!isEditingAssignment) {
                      setSelectedUserId(event.assignedTo?.id || null);
                    }
                  }}
                  css={css({ marginLeft: '8px' })}
                >
                  {isEditingAssignment ? t('common:actions.cancel') : t('details.changeAssignment')}
                </Button>
              )}
            </Title>
            <Descriptions column={1} bordered size="small">
              <Descriptions.Item label={t('details.assigned')}>
                {isEditingAssignment ? (
                  <Space>
                    <Select
                      css={css({ width: 300 })}
                      placeholder={t('details.selectAssignee')}
                      value={selectedUserId}
                      onChange={setSelectedUserId}
                      allowClear
                      showSearch
                      filterOption={(input, option) =>
                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                      }
                      options={[
                        { value: null, label: t('details.unassignedOption') },
                        ...(farmUsers || []).map((item) => ({
                          value: item.user.id,
                          label: `${item.user.username || item.user.email} (${item.membership.role.code})`,
                        })),
                      ]}
                    />
                    <Button
                      type="primary"
                      size="small"
                      loading={isUpdating}
                      onClick={async () => {
                        try {
                          await updateEvent({
                            saveId: save.id,
                            eventId: event.id,
                            payload: { assignedTo: selectedUserId || undefined },
                          }).unwrap();
                          message.success(t('details.assignmentChanged'));
                          setIsEditingAssignment(false);
                        } catch (error) {
                          message.error(t('details.assignmentFailed'));
                        }
                      }}
                    >
                      {t('common:actions.save')}
                    </Button>
                  </Space>
                ) : event.assignedTo ? (
                  <Space direction="vertical" size={0}>
                    <Text>{event.assignedTo.name}</Text>
                    {event.assignedAt && (
                      <Text type="secondary" css={commonStyles.tinyText}>
                        {formatDateTime(event.assignedAt, 'short')}
                      </Text>
                    )}
                  </Space>
                ) : (
                  <Text type="secondary">{t('details.unassigned')}</Text>
                )}
              </Descriptions.Item>
              {event.completedBy && (
                <Descriptions.Item label={t('details.completed')}>
                  <Space direction="vertical" size={0}>
                    <Text>{t('details.userId', { id: event.completedBy })}</Text>
                    {event.actualDate && (
                      <Text type="secondary" css={commonStyles.tinyText}>
                        {formatDateTime(event.actualDate, 'short')}
                      </Text>
                    )}
                  </Space>
                </Descriptions.Item>
              )}
            </Descriptions>
          </div>

          {/* Batch информация */}
          {event.isBatch && (
            <div>
              <Title level={5}>{t('details.batchActivity')}</Title>
              <Descriptions column={1} bordered size="small">
                <Descriptions.Item label={t('common:labels.type')}>
                  <Tag color="purple">{t('details.parentContainer')}</Tag>
                </Descriptions.Item>
                {event.batchTotal !== undefined && (
                  <Descriptions.Item label={t('details.childTotal')}>
                    {event.batchTotal}
                  </Descriptions.Item>
                )}
                {event.batchCompleted !== undefined && (
                  <Descriptions.Item label={t('details.completed')}>
                    {event.batchCompleted} / {event.batchTotal}
                  </Descriptions.Item>
                )}
                {event.batchProgress !== undefined && (
                  <Descriptions.Item label={t('details.progress')}>
                    {event.batchProgress}%
                  </Descriptions.Item>
                )}
              </Descriptions>
            </div>
          )}

          {/* Атрибуты события */}
          {event.properties && event.properties.length > 0 && (
            <div>
              <Title level={5}>{t('details.attributesAndMeasurements')}</Title>
              <Descriptions column={1} bordered size="small">
                {event.properties.map((attr: any) => (
                  <Descriptions.Item
                    key={attr.propertyId}
                    label={
                      <Space>
                        <Text strong>{attr.propertyName}</Text>
                        {attr.propertyCode && (
                          <Text type="secondary" css={commonStyles.tinyText}>
                            ({attr.propertyCode})
                          </Text>
                        )}
                      </Space>
                    }
                  >
                    {attr.valueNumber !== null && attr.valueNumber !== undefined && (
                      <Tag color="blue">{attr.valueNumber}</Tag>
                    )}
                    {attr.valueText && <Tag color="green">{attr.valueText}</Tag>}
                    {attr.valueBoolean !== null && attr.valueBoolean !== undefined && (
                      <Tag color={attr.valueBoolean ? 'success' : 'default'}>
                        {attr.valueBoolean ? t('common:actions.yes') : t('common:actions.no')}
                      </Tag>
                    )}
                  </Descriptions.Item>
                ))}
              </Descriptions>
            </div>
          )}

          {/* ID информация */}
          <div>
            <Divider css={css({ margin: '8px 0' })} />
            <Space size="small" wrap>
              <Text type="secondary" css={commonStyles.tinyText}>
                {t('details.eventId', { id: event.id })}
              </Text>
              {event.plantingId && (
                <Text type="secondary" css={commonStyles.tinyText}>
                  | {t('details.plantingId', { id: event.plantingId })}
                </Text>
              )}
              {event.parentEventId && (
                <Text type="secondary" css={commonStyles.tinyText}>
                  | {t('details.parentEvent', { id: event.parentEventId })}
                </Text>
              )}
            </Space>
          </div>
        </Space>
      )}
    </Modal>
  );
};
